import { useCallback, useState, useEffect, useMemo, useRef } from "react";

import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";

import SubscriptionLunaKeysTable from "../components/billing/SubscriptionLunaKeysTable";
import CustomerNotesTab from "../components/CustomerNotesTab";
import NewTicketModal from "../components/NewTicketModal";
import PlaylistDevicesModal from "../components/PlaylistDevicesModal";
import ResellerManagedBadge, {
  CustomerManagedBadge,
} from "../components/ResellerManagedBadge";
import API_URL from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import useRouteTab from "../hooks/useRouteTab";
import apiService from "../services/api";
import { mapCustomerTransactionsToInvoices } from "../utils/customerDetailsInvoices";
import {
  countCustomerLunaKeyStatuses,
  getLunaKeyManagedLabel,
  isResellerManagedLunaKey,
  lunaKeyMatchesSearch,
  mergeCustomerLunaKeys,
} from "../utils/customerLunaKeys";
import { shouldDeferUnauthorizedRecovery } from "../utils/impersonation";
import { buildDetailNavigationState } from "../utils/navigation";
import {
  DEFAULT_REDEEM_CODE_STATUS_FILTERS,
  filterRedeemCodesByStatuses,
  formatRedeemCodeStatus,
  getRedeemCodeStatusClass,
  getRedeemCodeStatusFilterLabel,
  getRedeemCodeStatusOptions,
  normalizeRedeemCodeStatus,
} from "../utils/redeemCodeStatusFilters";

// Fix for React 18 StrictMode
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const shouldDismissBackdropOnKeyDown = (event) =>
  event.key === "Escape" ||
  ((event.key === "Enter" || event.key === " ") &&
    event.target === event.currentTarget);

const getRedeemCodeLinkedPlaylists = (redeemCode) => {
  if (Array.isArray(redeemCode?.linkedPlaylists)) {
    return redeemCode.linkedPlaylists;
  }

  if (redeemCode?.user) {
    return [
      {
        domain: redeemCode.user.domain,
        id: redeemCode.user.user_id,
        name:
          redeemCode.user.name ||
          redeemCode.user.accountName ||
          redeemCode.user.username ||
          "Playlist",
        userId: redeemCode.user.user_id,
        username: redeemCode.user.username,
      },
    ];
  }

  return [];
};

const getRedeemCodePlaylistKey = (playlist) =>
  playlist?.id ||
  playlist?.userId ||
  playlist?.user_id ||
  [playlist?.accountId, playlist?.domain, playlist?.username]
    .filter(Boolean)
    .join(":");

const getPlaylistDisplayName = (playlist) =>
  playlist?.name ||
  playlist?.playlistName ||
  playlist?.accountName ||
  playlist?.username ||
  "Playlist";

const formatCustomerLastLoginDate = (value) => {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const RESELLER_LOG_SORT_FIELD_MAP = Object.freeze({
  actionType: "action_type",
  createdAt: "created_at",
});

const CUSTOMER_DETAIL_TAB_IDS = [
  "summary",
  "invoices",
  "subscriptions",
  "redeemcodes",
  "tickets",
  "notes",
  "log",
];

const getVisiblePageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 1) return [];

  const pages = new Set([1, totalPages]);

  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) {
      pages.add(page);
    }
  }

  return Array.from(pages).sort((a, b) => a - b);
};

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, startImpersonation } = useAuth();
  const [activeTab, setActiveTab] = useRouteTab({
    defaultTab: "summary",
    validTabs: CUSTOMER_DETAIL_TAB_IDS,
  });
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerNotes, setCustomerNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const notesRequestSeq = useRef(0);
  const resellerLogsRequestSeq = useRef(0);
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isStartingImpersonation, setIsStartingImpersonation] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const [widgets, setWidgets] = useState(() => {
    // payMethods (unsupported) and adminNotes (replaced by the Notes tab)
    // no longer exist; strip them from any saved layout.
    const validWidgetIds = ["clientInfo", "otherInfo", "adminActions"];
    const savedWidgets = localStorage.getItem(`customerWidgets_${id}`);

    if (savedWidgets) {
      try {
        const parsed = JSON.parse(savedWidgets);

        for (const column of Object.keys(parsed)) {
          parsed[column] = (parsed[column] || []).filter((widgetId) =>
            validWidgetIds.includes(widgetId)
          );
        }

        // Ensure adminActions is included if user is admin
        if (!parsed.column3.includes("adminActions")) {
          parsed.column3 = ["adminActions", ...parsed.column3];
        }

        return parsed;
      } catch (e) {
        console.error("Error loading saved widget positions:", e);
      }
    }

    return {
      column1: ["clientInfo"],
      column2: ["otherInfo"],
      column3: ["adminActions"],
    };
  });

  // Tab data states
  const [tickets, setTickets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [_productServices, setProductServices] = useState([]);
  const [_recentEmails, setRecentEmails] = useState([]);
  const [_allEmails, setAllEmails] = useState([]);
  const [loadingTabData, setLoadingTabData] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEmailView, setShowEmailView] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [resellerLogs, setResellerLogs] = useState([]);
  const [resellerLogsLoading, setResellerLogsLoading] = useState(false);
  const [resellerLogsSearchQuery, setResellerLogsSearchQuery] = useState("");
  const [resellerLogsSortField, setResellerLogsSortField] =
    useState("createdAt");
  const [resellerLogsSortDirection, setResellerLogsSortDirection] =
    useState("desc");
  const [resellerLogsPage, setResellerLogsPage] = useState(1);
  const [resellerLogsPerPage, setResellerLogsPerPage] = useState(10);
  const [resellerLogsPagination, setResellerLogsPagination] = useState({
    limit: 10,
    page: 1,
    pages: 0,
    total: 0,
  });
  const [redeemCodes, setRedeemCodes] = useState([]);
  const [linkedRedeemCodes, setLinkedRedeemCodes] = useState([]);
  const lunaKeyTableCodes = useMemo(
    () => mergeCustomerLunaKeys(redeemCodes, linkedRedeemCodes),
    [linkedRedeemCodes, redeemCodes]
  );
  const [redeemCodesPage, setRedeemCodesPage] = useState(1);
  const [redeemCodesPerPage, setRedeemCodesPerPage] = useState(10);
  const [redeemCodesSortField, setRedeemCodesSortField] = useState(null);
  const [redeemCodesSortDirection, setRedeemCodesSortDirection] =
    useState("asc");
  const [redeemCodesStatusFilter, setRedeemCodesStatusFilter] = useState([
    ...DEFAULT_REDEEM_CODE_STATUS_FILTERS,
  ]);
  const [showRedeemCodesStatusDropdown, setShowRedeemCodesStatusDropdown] =
    useState(false);
  const [redeemCodesSearchQuery, setRedeemCodesSearchQuery] = useState("");
  const redeemCodeStatusDropdownRef = useRef(null);
  const [showPageInput, setShowPageInput] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const [showRedeemCodeModal, setShowRedeemCodeModal] = useState(false);
  const [selectedRedeemCode, setSelectedRedeemCode] = useState(null);
  const [_loadingRedeemCodeDetail, setLoadingRedeemCodeDetail] =
    useState(false);
  const [showPlaylistDevicesModal, setShowPlaylistDevicesModal] =
    useState(false);
  const [playlistDevicesModalPlaylist, setPlaylistDevicesModalPlaylist] =
    useState(null);
  const [playlistDevicesModalLoading, setPlaylistDevicesModalLoading] =
    useState(false);
  const [playlistDevicesModalItems, setPlaylistDevicesModalItems] = useState(
    []
  );
  const [showResellerConfirmModal, setShowResellerConfirmModal] =
    useState(false);
  const [pendingResellerAction, setPendingResellerAction] = useState(null);
  const [showSuspendConfirmModal, setShowSuspendConfirmModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const invoices = useMemo(
    () => mapCustomerTransactionsToInvoices(transactions, redeemCodes),
    [redeemCodes, transactions]
  );
  const [pendingSuspendAction, setPendingSuspendAction] = useState(null);
  const [newProduct, setNewProduct] = useState({
    productName: "Lunova Player Premium",
    productType: "app_access",
    quantity: 1,
    price: 9.99,
    recurringAmount: 9.99,
    billingCycle: "monthly",
    packageType: "premium",
    paymentMethod: "stripe",
    promotionCode: "",
  });

  const fetchCustomerSubscriptionLunaKeys = useCallback(
    (params) => apiService.getCustomerSubscriptionLunaKeys(id, params),
    [id]
  );

  const cancelCustomerSubscriptionLunaKey = useCallback(
    (redeemCodeId, options) =>
      apiService.cancelCustomerSubscriptionLunaKey(id, redeemCodeId, options),
    [id]
  );

  // States for client info editing
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editedClient, setEditedClient] = useState(null);

  // Handle sorting for redeem codes
  const handleRedeemCodesSort = (field) => {
    if (redeemCodesSortField === field) {
      // Toggle direction if same field
      setRedeemCodesSortDirection(
        redeemCodesSortDirection === "asc" ? "desc" : "asc"
      );
    } else {
      // New field, default to ascending
      setRedeemCodesSortField(field);
      setRedeemCodesSortDirection("asc");
    }
    setRedeemCodesPage(1); // Reset to first page when sorting
  };

  // Fetch redeem code details
  const fetchRedeemCodeDetails = async (redeemCodeId) => {
    setLoadingRedeemCodeDetail(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}/v1/dashboard/redeem-codes/${redeemCodeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === 200) {
        setSelectedRedeemCode(response.data.data);
        setShowRedeemCodeModal(true);
      }
    } catch (error) {
      console.error("Error fetching redeem code details:", error);
      toast.error("Failed to load redeem code details");
    } finally {
      setLoadingRedeemCodeDetail(false);
    }
  };

  const handleShowPlaylistDevices = async (playlist) => {
    const playlistId = playlist?.userId || playlist?.user_id || playlist?.id;

    if (!playlistId) {
      toast.error("No playlist is linked to this LunaKey");

      return;
    }

    setPlaylistDevicesModalPlaylist(playlist);
    setPlaylistDevicesModalItems([]);
    setShowPlaylistDevicesModal(true);
    setPlaylistDevicesModalLoading(true);

    try {
      const response = await apiService.getCustomerPlaylistDevices(
        id,
        playlistId
      );

      setPlaylistDevicesModalItems(response?.data?.devices || []);
    } catch (error) {
      toast.error(error.message || "Failed to load devices");
    } finally {
      setPlaylistDevicesModalLoading(false);
    }
  };

  const closePlaylistDevicesModal = () => {
    setShowPlaylistDevicesModal(false);
    setPlaylistDevicesModalPlaylist(null);
    setPlaylistDevicesModalItems([]);
  };

  // Get sorted and filtered redeem codes
  const sortedRedeemCodes = useMemo(() => {
    // First, apply search filter if present
    let filtered = lunaKeyTableCodes;

    // Client-side search filter (fuzzy search through all fields)
    if (redeemCodesSearchQuery && redeemCodesSearchQuery.trim()) {
      const searchTerm = redeemCodesSearchQuery.toLowerCase().trim();

      filtered = filtered.filter((code) =>
        lunaKeyMatchesSearch(code, searchTerm)
      );
    }

    // Then, filter by the selected real status codes.
    filtered = filterRedeemCodesByStatuses(filtered, redeemCodesStatusFilter);

    // Then, sort if a sort field is selected
    if (!redeemCodesSortField) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (redeemCodesSortField) {
        case "type":
          aValue = a.redeem_type || "";
          bValue = b.redeem_type || "";
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "managed":
          aValue = getLunaKeyManagedLabel(a);
          bValue = getLunaKeyManagedLabel(b);
          break;
        case "redeemed":
          aValue = a.date_redeemed ? new Date(a.date_redeemed).getTime() : 0;
          bValue = b.date_redeemed ? new Date(b.date_redeemed).getTime() : 0;
          break;
        case "expires":
          aValue = a.expires_at ? new Date(a.expires_at).getTime() : 0;
          bValue = b.expires_at ? new Date(b.expires_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return redeemCodesSortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return redeemCodesSortDirection === "asc" ? 1 : -1;

      return 0;
    });

    return sorted;
  }, [
    lunaKeyTableCodes,
    redeemCodesSearchQuery,
    redeemCodesSortDirection,
    redeemCodesSortField,
    redeemCodesStatusFilter,
  ]);

  const lunaKeyStatusCounts = useMemo(
    () => countCustomerLunaKeyStatuses(redeemCodes, linkedRedeemCodes),
    [linkedRedeemCodes, redeemCodes]
  );

  const handleRedeemCodeStatusToggle = (status) => {
    const normalizedStatus = normalizeRedeemCodeStatus(status);

    if (!normalizedStatus) return;

    setRedeemCodesStatusFilter((current) => {
      const selected = new Set(
        (current || []).map(normalizeRedeemCodeStatus).filter(Boolean)
      );

      if (selected.has(normalizedStatus)) {
        selected.delete(normalizedStatus);
      } else {
        selected.add(normalizedStatus);
      }

      return getRedeemCodeStatusOptions(lunaKeyTableCodes)
        .map((option) => option.value)
        .filter((optionStatus) => selected.has(optionStatus));
    });
    setRedeemCodesPage(1);
  };

  const handleLunaKeyStatusSummaryClick = (status) => {
    const normalizedStatus = normalizeRedeemCodeStatus(status);

    if (!normalizedStatus) return;

    setRedeemCodesStatusFilter([normalizedStatus]);
    setRedeemCodesPage(1);
    setShowRedeemCodesStatusDropdown(false);
    setActiveTab("redeemcodes");
  };

  const fetchAllClients = async () => {
    try {
      const response = await apiService.getCustomers();
      const customers = response.data || response.customers || response;

      setAllClients(Array.isArray(customers) ? customers : []);
    } catch (error) {
      console.error("Error fetching all clients:", error);
      setAllClients([]);
    }
  };

  const fetchResellerLogs = useCallback(async () => {
    const resellerId = client?.resellerId;

    if (!resellerId) {
      setResellerLogs([]);
      setResellerLogsPagination({
        limit: resellerLogsPerPage,
        page: 1,
        pages: 0,
        total: 0,
      });

      return;
    }

    const requestId = resellerLogsRequestSeq.current + 1;

    resellerLogsRequestSeq.current = requestId;
    setResellerLogsLoading(true);

    try {
      const params = {
        limit: resellerLogsPerPage,
        order: resellerLogsSortDirection.toUpperCase(),
        page: resellerLogsPage,
        resellerId,
        sort:
          RESELLER_LOG_SORT_FIELD_MAP[resellerLogsSortField] || "created_at",
      };

      if (resellerLogsSearchQuery.trim()) {
        params.search = resellerLogsSearchQuery.trim();
      }

      const response = await apiService.getResellerLogs(params);

      if (requestId !== resellerLogsRequestSeq.current) return;

      const logs = response?.data?.logs || [];
      const pagination = response?.pagination || {
        limit: resellerLogsPerPage,
        page: resellerLogsPage,
        pages: 0,
        total: 0,
      };

      setResellerLogs(Array.isArray(logs) ? logs : []);
      setResellerLogsPagination({
        limit: pagination.limit || resellerLogsPerPage,
        page: pagination.page || resellerLogsPage,
        pages: pagination.pages || 0,
        total: pagination.total || 0,
      });
    } catch (error) {
      if (requestId !== resellerLogsRequestSeq.current) return;

      console.error("Error fetching reseller logs:", error);
      setResellerLogs([]);
      setResellerLogsPagination({
        limit: resellerLogsPerPage,
        page: resellerLogsPage,
        pages: 0,
        total: 0,
      });
      toast.error("Failed to load reseller logs");
    } finally {
      if (requestId === resellerLogsRequestSeq.current) {
        setResellerLogsLoading(false);
      }
    }
  }, [
    client?.resellerId,
    resellerLogsPage,
    resellerLogsPerPage,
    resellerLogsSearchQuery,
    resellerLogsSortDirection,
    resellerLogsSortField,
  ]);

  const handleResellerLogSort = (field) => {
    setResellerLogsPage(1);
    setResellerLogsSortField((currentField) => {
      if (currentField !== field) {
        setResellerLogsSortDirection("asc");

        return field;
      }

      setResellerLogsSortDirection((direction) =>
        direction === "asc" ? "desc" : "asc"
      );

      return currentField;
    });
  };

  const fetchClientData = async () => {
    setLoading(true);

    try {
      const response = await apiService.getCustomer(id);
      const customerData = response.data || response;

      if (!customerData) {
        throw new Error("User not found");
      }

      // Parse the customer data and add additional fields

      // Handle name parsing - it might be in different formats
      let firstName = "";
      let lastName = "";

      if (customerData.name) {
        const nameParts = customerData.name.split(" ");

        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      } else if (customerData.firstName || customerData.lastName) {
        firstName = customerData.firstName || "";
        lastName = customerData.lastName || "";
      } else if (customerData.email) {
        // Use email username as fallback
        firstName = customerData.email.split("@")[0];
      }

      const lastLoginValue =
        customerData.lastLoginAt ||
        customerData.lastLoginDate ||
        customerData.lastLogin ||
        null;
      const lastLoginIp =
        customerData.lastLoginIp || customerData.ipAddress || null;

      setClient({
        id: customerData.id,
        firstName: firstName || "Not provided",
        lastName: lastName || "",
        companyName: customerData.company || customerData.companyName || "",
        email: customerData.email || "Not provided",
        address1: customerData.address1 || customerData.address || "",
        address2: customerData.address2 || "",
        city: customerData.city || "",
        state: customerData.state || "",
        postcode:
          customerData.postcode ||
          customerData.postalCode ||
          customerData.zipCode ||
          "",
        country: customerData.country || "",
        phone: customerData.phone || customerData.phoneNumber || "",
        position: customerData.position || "",
        location: customerData.location || "",
        website: customerData.website || "",
        timezone: customerData.timezone || "",
        bio: customerData.bio || "",
        status: customerData.status || "Active",
        clientGroup: customerData.clientGroup || "None",
        isReseller: customerData.isReseller || false,
        resellerId: customerData.resellerId || null,
        resellerStatus: customerData.resellerStatus || null,
        resellerType: customerData.resellerType || "regular",
        resellerTier: customerData.resellerTier || null,
        type: customerData.type || "Free User",
        resellerManaged: customerData.resellerManaged === true,
        signupDate: customerData.createdAt
          ? new Date(customerData.createdAt).toLocaleDateString("en-US")
          : "Not available",
        clientFor: calculateClientDuration(customerData.createdAt),
        lastLogin: lastLoginValue || "Not available",
        lastLoginDate: formatCustomerLastLoginDate(lastLoginValue),
        lastLoginIp: lastLoginIp || "Not available",
        totalTickets: customerData.totalTickets || 0,
        totalSpent: customerData.totalSpent || "$0.00",
        payments: customerData.payments || 0,
        // Only include real invoice data if available
        invoices: customerData.invoices || {
          paid: { count: 0, amount: 0 },
          draft: { count: 0, amount: 0 },
          unpaid: { count: 0, amount: 0 },
          cancelled: { count: 0, amount: 0 },
          refunded: { count: 0, amount: 0 },
          collections: { count: 0, amount: 0 },
        },
        // Only include real income data if available
        income: customerData.income || {
          gross: 0,
          expenses: 0,
          net: 0,
          credit: 0,
        },
        // Only include real products data if available
        products: customerData.products || {
          sharedHosting: { count: 0, total: 0 },
          resellerHosting: { count: 0, total: 0 },
          vpsServer: { count: 0, total: 0 },
          productService: { count: 0, total: 0 },
          addon: { count: 0, total: 0 },
          acceptedQuotes: { count: 0, total: 0 },
          supportTickets: { count: 0, total: 0 },
          affiliateSignups: { count: 0 },
        },
        // Only include real recent emails if available
        recentEmails: customerData.recentEmails || [],
      });

      // Set redeem codes if they exist in the API response
      if (customerData.redeemCodes && Array.isArray(customerData.redeemCodes)) {
        setRedeemCodes(customerData.redeemCodes);
      } else {
        setRedeemCodes([]);
      }

      // Reseller LunaKeys reaching this customer through managed playlist
      // links. Kept apart from redeemCodes so only the LunaKeys table and the
      // claimed count treat them as this customer's.
      setLinkedRedeemCodes(
        Array.isArray(customerData.linkedRedeemCodes)
          ? customerData.linkedRedeemCodes
          : []
      );

      // Set transactions if they exist in the API response
      if (
        customerData.transactions &&
        Array.isArray(customerData.transactions)
      ) {
        setTransactions(customerData.transactions);
      } else {
        setTransactions([]);
      }

      // Set tickets if they exist in the API response
      if (customerData.tickets && Array.isArray(customerData.tickets)) {
        setTickets(customerData.tickets);
      } else {
        setTickets([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error in fetchClientData:", error);
      setLoading(false);

      // Only show authentication errors and redirect for 401
      if (error.response?.status === 401) {
        if (shouldDeferUnauthorizedRecovery()) {
          return;
        }

        toast.error("Authentication failed. Please log in again.", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => navigate("/login"), 2000);
      } else if (error.response?.status === 403) {
        toast.error("You do not have permission to view this user/customer.", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => navigate(-1), 2000);
      } else if (error.response?.status === 404) {
        toast.error("User not found.", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => navigate(-1), 2000);
      } else {
        toast.error("Failed to load user data.", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => navigate(-1), 2000);
      }
    }
  };

  const calculateClientDuration = (createdAt) => {
    if (!createdAt) return "0 Months";
    const created = new Date(createdAt);
    const now = new Date();
    const months =
      (now.getFullYear() - created.getFullYear()) * 12 +
      (now.getMonth() - created.getMonth());

    if (months < 1) return "Less than 1 Month";
    if (months === 1) return "1 Month";

    return `${months} Months`;
  };

  const handleClientChange = (clientId) => {
    setShowDropdown(false);
    setSearchTerm("");
    navigate(`/customers/${clientId}`);
  };

  const handleViewInvoice = (transactionId) => {
    navigate(`/invoices/${transactionId}`, {
      state: buildDetailNavigationState(location),
    });
  };

  // Fetch tickets for current customer
  const fetchTickets = async () => {
    // Tickets are already loaded from the initial customer data API call
    // Just set loading to false since data is already available
    setLoadingTabData(false);
  };

  // Fetch product services for current customer
  const fetchProductServices = async () => {
    setLoadingTabData(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}/v1/dashboard/product-services/customer/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Handle both array and object with data property
      const products = Array.isArray(response.data)
        ? response.data
        : response.data?.data
          ? response.data.data
          : response.data?.products
            ? response.data.products
            : [];

      setProductServices(products);
    } catch (error) {
      console.error("Error fetching product services:", error);
      setProductServices([]);
    } finally {
      setLoadingTabData(false);
    }
  };

  // Create new product
  const handleCreateProduct = async () => {
    try {
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${API_URL}/v1/dashboard/product-services`,
        { ...newProduct, customer: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Product created successfully");
      setShowProductModal(false);
      setNewProduct({
        productName: "Lunova Player Premium",
        productType: "app_access",
        quantity: 1,
        price: 9.99,
        recurringAmount: 9.99,
        billingCycle: "monthly",
        packageType: "premium",
        paymentMethod: "stripe",
        promotionCode: "",
      });
      fetchProductServices();
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("Failed to create product");
    }
  };

  // Handle suspend/unsuspend customer
  const handleSuspendToggle = async () => {
    const isSuspended = pendingSuspendAction;
    const action = isSuspended ? "suspend" : "unsuspend";
    const actionPastTense = isSuspended ? "suspended" : "unsuspended";

    try {
      await apiService.setCustomerSuspension(id, isSuspended);

      // Refresh customer data from API to get latest details
      await fetchClientData();

      toast.success(
        `${client.firstName} ${client.lastName} has been ${actionPastTense}`
      );

      // Close modal
      setShowSuspendConfirmModal(false);
      setPendingSuspendAction(null);
    } catch (error) {
      console.error(`Error ${action}ing customer:`, error);
      toast.error(`Failed to ${action} customer`);
    }
  };

  const handleUpdateResellerStatus = async ({
    isReseller,
    resellerType,
    successMessage,
  }) => {
    try {
      await apiService.updateCustomerResellerStatus(id, {
        isReseller,
        resellerType,
      });
      await fetchClientData();
      toast.success(successMessage);
      setShowResellerConfirmModal(false);
      setPendingResellerAction(null);
    } catch (error) {
      console.error("Error updating reseller status:", error);
      toast.error(
        error.response?.data?.error || "Failed to update reseller status"
      );
    }
  };

  // Fetch recent emails for customer
  const fetchRecentEmails = async () => {
    try {
      const token = localStorage.getItem("authToken");
      let customerEmails = [];

      // Try multiple endpoints to get email/message data

      // 1. Try direct emails endpoint
      try {
        const response = await axios.get(`${API_URL}/v1/dashboard/emails`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Handle different response formats
        const allEmails = Array.isArray(response.data)
          ? response.data
          : response.data?.data
            ? response.data.data
            : response.data?.emails
              ? response.data.emails
              : [];

        // Filter emails for this customer
        customerEmails = allEmails
          .filter((email) => {
            const emailRecipient =
              email.to || email.recipient || email.customer || email.customerId;
            const emailRecipientId =
              typeof emailRecipient === "object"
                ? emailRecipient.id
                : emailRecipient;

            return (
              emailRecipientId === id || email.customerEmail === client?.email
            );
          })
          .sort(
            (a, b) =>
              new Date(b.date || b.createdAt || b.sentAt) -
              new Date(a.date || a.createdAt || a.sentAt)
          )
          .slice(0, 4)
          .map((email) => ({
            id: email.id || email._id,
            subject: email.subject || "No Subject",
            date:
              email.date || email.sentAt || email.createdAt
                ? new Date(
                    email.date || email.sentAt || email.createdAt
                  ).toLocaleDateString()
                : "Unknown Date",
            ticketId: email.ticketId || email.ticket_id || "N/A",
          }));
      } catch (emailError) {
        // 2. Try to get emails from tickets if direct endpoint fails
        try {
          const ticketsResponse = await axios.get(
            `${API_URL}/v1/dashboard/tickets`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const tickets = Array.isArray(ticketsResponse.data)
            ? ticketsResponse.data
            : ticketsResponse.data?.data || ticketsResponse.data?.tickets || [];

          // Filter tickets for this customer
          const customerTickets = tickets.filter((ticket) => {
            const ticketCustomer =
              ticket.customer ||
              ticket.customerId ||
              ticket.userId ||
              ticket.user;
            const customerId =
              typeof ticketCustomer === "object"
                ? ticketCustomer.id
                : ticketCustomer;

            return customerId === id;
          });

          // Convert recent tickets to email format
          customerEmails = customerTickets.slice(0, 4).map((ticket) => ({
            id: ticket.id || ticket._id,
            subject:
              ticket.subject ||
              ticket.title ||
              `Support Ticket: ${ticket.ticketNumber || ticket.id}`,
            date:
              ticket.createdAt || ticket.created_at || ticket.date
                ? new Date(
                    ticket.createdAt || ticket.created_at || ticket.date
                  ).toLocaleDateString()
                : new Date().toLocaleDateString(),
            ticketId: ticket.ticketNumber || ticket.id,
          }));
        } catch (ticketError) {}
      }

      if (customerEmails.length > 0) {
        // Set recent emails (last 4)
        setRecentEmails(customerEmails);
        setClient((prev) => ({ ...prev, recentEmails: customerEmails }));

        // Also set all emails for the emails tab (fetch more if needed)
        setAllEmails(customerEmails);
      } else {
        // Set empty state if no emails found
        setRecentEmails([]);
        setAllEmails([]);
        setClient((prev) => ({ ...prev, recentEmails: [] }));
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      // Set empty array on complete failure
      setRecentEmails([]);
      setClient((prev) => ({ ...prev, recentEmails: [] }));
    }
  };

  // Fetch admin notes for this customer (ordered latest-updated first by the
  // API, so index 0 also drives the banner above the stat boxes).
  const fetchCustomerNotes = async (
    customerId = id,
    { clearExisting = false } = {}
  ) => {
    const requestSeq = notesRequestSeq.current + 1;

    notesRequestSeq.current = requestSeq;
    setNotesLoading(true);
    if (clearExisting) {
      setCustomerNotes([]);
    }
    try {
      const response = await apiService.getCustomerNotes(customerId);

      if (requestSeq !== notesRequestSeq.current) return;
      setCustomerNotes(response?.data?.notes || []);
    } catch (error) {
      if (requestSeq !== notesRequestSeq.current) return;
      console.error("Error fetching admin notes:", error);
      toast.error("Failed to load admin notes");
      setCustomerNotes([]);
    } finally {
      if (requestSeq === notesRequestSeq.current) {
        setNotesLoading(false);
      }
    }
  };

  const handleCreateNote = async (message) => {
    try {
      await apiService.createCustomerNote(id, { message });
    } catch (error) {
      toast.error(error?.message || "Failed to add note");
      throw error;
    }
    toast.success("Note added");
    await fetchCustomerNotes(id);
  };

  const handleUpdateNote = async (noteId, message) => {
    try {
      await apiService.updateCustomerNote(id, noteId, { message });
    } catch (error) {
      toast.error(error?.message || "Failed to update note");
      throw error;
    }
    toast.success("Note updated");
    await fetchCustomerNotes(id);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await apiService.deleteCustomerNote(id, noteId);
    } catch (error) {
      toast.error(error?.message || "Failed to delete note");
      throw error;
    }
    toast.success("Note deleted");
    await fetchCustomerNotes(id);
  };

  const formatNoteTimestamp = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // useEffect hooks must be after all function definitions
  useEffect(() => {
    setRedeemCodesStatusFilter([...DEFAULT_REDEEM_CODE_STATUS_FILTERS]);
    setShowRedeemCodesStatusDropdown(false);
    setRedeemCodesPage(1);
    resellerLogsRequestSeq.current += 1;
    setResellerLogs([]);
    setResellerLogsLoading(false);
    setResellerLogsSearchQuery("");
    setResellerLogsSortField("createdAt");
    setResellerLogsSortDirection("desc");
    setResellerLogsPage(1);
    setResellerLogsPerPage(10);
    setResellerLogsPagination({
      limit: 10,
      page: 1,
      pages: 0,
      total: 0,
    });
    fetchClientData();
    fetchAllClients();
    fetchProductServices(); // Fetch products for the widget
    fetchRecentEmails(); // Fetch recent emails
    fetchCustomerNotes(id, { clearExisting: true }); // Fetch admin notes for the banner + Notes tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showResellerLogTab = Boolean(client?.isReseller && client?.resellerId);

  useEffect(() => {
    if (client && activeTab === "log" && !showResellerLogTab) {
      setActiveTab("summary", { replace: true });
    }
  }, [activeTab, client, setActiveTab, showResellerLogTab]);

  useEffect(() => {
    if (activeTab !== "log" || !showResellerLogTab) return undefined;

    const timeoutId = setTimeout(
      () => fetchResellerLogs(),
      resellerLogsSearchQuery.trim() ? 350 : 0
    );

    return () => clearTimeout(timeoutId);
  }, [
    activeTab,
    fetchResellerLogs,
    resellerLogsSearchQuery,
    showResellerLogTab,
  ]);

  useEffect(() => {
    if (!showRedeemCodesStatusDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (
        redeemCodeStatusDropdownRef.current &&
        !redeemCodeStatusDropdownRef.current.contains(event.target)
      ) {
        setShowRedeemCodesStatusDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowRedeemCodesStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showRedeemCodesStatusDropdown]);

  // Handle LunaKey query parameter - automatically open modal when navigating from search
  useEffect(() => {
    const lunaKeyId = searchParams.get("lunakey");

    if (lunaKeyId && client) {
      // Fetch and show the modal (don't switch tabs, modal opens on top)
      fetchRedeemCodeDetails(lunaKeyId);
      // Clean up URL parameter after opening modal
      const nextSearchParams = new URLSearchParams(searchParams);

      nextSearchParams.delete("lunakey");
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [searchParams, client, setSearchParams]);

  // Initialize editedClient when client data is loaded
  useEffect(() => {
    if (client) {
      setEditedClient({
        firstName: client.firstName || "",
        lastName: client.lastName || "",
        email: client.email || "",
        phone: client.phone || "",
        companyName: client.companyName || "",
        position: client.position || "",
        location: client.location || "",
        website: client.website || "",
        timezone: client.timezone || "",
        bio: client.bio || "",
      });
    }
  }, [client]);

  // Fetch all emails for emails tab
  const fetchAllEmails = async () => {
    setLoadingTabData(true);

    const token = localStorage.getItem("authToken");
    let allCustomerEmails = [];

    // Try to fetch tickets
    try {
      const ticketsResponse = await axios.get(
        `${API_URL}/v1/dashboard/tickets`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const allTickets = Array.isArray(ticketsResponse.data)
        ? ticketsResponse.data
        : ticketsResponse.data?.data || ticketsResponse.data?.tickets || [];

      // Filter tickets for THIS specific customer
      const customerTickets = allTickets.filter((ticket) => {
        // Check various ID fields
        const ticketCustomerId =
          ticket.customer_id ||
          ticket.customerId ||
          ticket.user_id ||
          ticket.userId;

        // Check various email fields
        const ticketEmail =
          ticket.customerEmail ||
          ticket.customer_email ||
          ticket.email ||
          ticket.user_email;

        // Match by ID or email
        const matches =
          ticketCustomerId === id || ticketEmail === client?.email;

        if (matches) {
        }

        return matches;
      });

      // Convert only this customer's tickets to emails
      if (customerTickets.length > 0) {
        const ticketEmails = customerTickets.map((ticket) => ({
          id: `ticket-${ticket.id || ticket._id || ticket.ticketNumber}`,
          date:
            ticket.createdAt || ticket.created_at || new Date().toISOString(),
          subject: ticket.subject || ticket.title || `Ticket #${ticket.id}`,
          hasAttachment: false,
          from:
            ticket.customerEmail ||
            ticket.customer_email ||
            ticket.email ||
            client?.email ||
            "customer@example.com",
          to: "support@lunova.app",
          body: ticket.message || ticket.description || ticket.issue || "",
          type: "ticket",
        }));

        allCustomerEmails = [...allCustomerEmails, ...ticketEmails];
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }

    // Try to fetch emails
    try {
      const emailsResponse = await axios.get(`${API_URL}/v1/dashboard/emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allEmails = Array.isArray(emailsResponse.data)
        ? emailsResponse.data
        : emailsResponse.data?.data || emailsResponse.data?.emails || [];

      // Filter emails for THIS specific customer
      const customerEmails = allEmails.filter((email) => {
        // Check if email is TO or FROM this customer
        const emailTo =
          email.to ||
          email.to_email ||
          email.recipient ||
          email.recipient_email;
        const emailFrom =
          email.from || email.from_email || email.sender || email.sender_email;

        // Check customer ID fields
        const emailCustomerId =
          email.customer_id ||
          email.customerId ||
          email.user_id ||
          email.userId;

        // Match by email address or customer ID
        const matches =
          emailTo === client?.email ||
          emailFrom === client?.email ||
          emailCustomerId === id ||
          email.customerEmail === client?.email ||
          email.customer_email === client?.email;

        if (matches) {
        }

        return matches;
      });

      // Convert only this customer's emails
      if (customerEmails.length > 0) {
        const formattedEmails = customerEmails.map((email) => ({
          id: `email-${email.id || email._id}`,
          date:
            email.date ||
            email.sentAt ||
            email.createdAt ||
            new Date().toISOString(),
          subject: email.subject || email.title || "No Subject",
          hasAttachment: email.hasAttachment || false,
          from: email.from || email.sender || "system@lunova.app",
          to:
            email.to ||
            email.recipient ||
            client?.email ||
            "customer@example.com",
          body: email.body || email.content || email.message || "",
          type: "email",
        }));

        allCustomerEmails = [...allCustomerEmails, ...formattedEmails];
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
    }

    // Don't create any fake data - show real data only
    if (allCustomerEmails.length === 0) {
    }

    // Sort by date (newest first)
    allCustomerEmails.sort((a, b) => new Date(b.date) - new Date(a.date));

    setAllEmails(allCustomerEmails);
    setLoadingTabData(false);
  };

  // Fetch tab data when tab changes
  useEffect(() => {
    if (activeTab === "tickets") {
      fetchTickets();
    } else if (activeTab === "emails") {
      fetchAllEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  const filteredClients = Array.isArray(allClients)
    ? allClients.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.company?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    let updatedWidgets = { ...widgets };

    if (source.droppableId === destination.droppableId) {
      // Reordering within the same column
      const column = widgets[source.droppableId];
      const newColumn = Array.from(column);
      const [removed] = newColumn.splice(source.index, 1);

      newColumn.splice(destination.index, 0, removed);

      updatedWidgets[source.droppableId] = newColumn;
    } else {
      // Moving between columns
      const sourceColumn = widgets[source.droppableId];
      const destColumn = widgets[destination.droppableId];
      const newSourceColumn = Array.from(sourceColumn);
      const newDestColumn = Array.from(destColumn);

      const [removed] = newSourceColumn.splice(source.index, 1);

      newDestColumn.splice(destination.index, 0, removed);

      updatedWidgets = {
        ...widgets,
        [source.droppableId]: newSourceColumn,
        [destination.droppableId]: newDestColumn,
      };
    }

    setWidgets(updatedWidgets);

    if (user?.role === "admin") {
      localStorage.setItem(
        `customerWidgets_${id}`,
        JSON.stringify(updatedWidgets)
      );
    }
  };

  // Function to render widget content
  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case "clientInfo":
        const handleSaveClient = async () => {
          try {
            // Validate required fields
            if (!editedClient.firstName || !editedClient.email) {
              toast.error("First Name and Email are required");

              return;
            }

            // TODO: Add API call to save client data
            // await apiService.updateClient(id, editedClient);

            // Update local state
            setClient({ ...client, ...editedClient });
            setIsEditingClient(false);
            toast.success("Client information updated successfully");
          } catch (error) {
            toast.error("Failed to update client information");
            console.error(error);
          }
        };

        return (
          <div className="card p-6 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-primary-600">
                Client Information
              </h3>
              {!isEditingClient ? (
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setIsEditingClient(true)}
                >
                  <i className="fas fa-edit mr-1" />
                  Edit
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      setEditedClient({
                        firstName: client.firstName || "",
                        lastName: client.lastName || "",
                        email: client.email || "",
                        phone: client.phone || "",
                        companyName: client.companyName || "",
                        position: client.position || "",
                        location: client.location || "",
                        website: client.website || "",
                        timezone: client.timezone || "",
                        bio: client.bio || "",
                      });
                      setIsEditingClient(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveClient}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-0">
              {/* First Name - Required */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  First Name <span className="text-red-500">*</span>
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.firstName}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        firstName: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.firstName || "Not provided"}
                  </span>
                )}
              </div>

              {/* Last Name */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Last Name
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.lastName}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        lastName: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.lastName || "Not provided"}
                  </span>
                )}
              </div>

              {/* Email - Required */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Email Address <span className="text-red-500">*</span>
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="email"
                    value={editedClient.email}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        email: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span
                    className="font-medium truncate select-text"
                    title={client.email}
                  >
                    {client.email || "Not provided"}
                  </span>
                )}
              </div>

              {/* Phone Number */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Phone Number
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="tel"
                    value={editedClient.phone}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        phone: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.phone || "Not provided"}
                  </span>
                )}
              </div>

              {/* Company */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Company
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.companyName}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        companyName: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span
                    className="font-medium truncate select-text"
                    title={client.companyName}
                  >
                    {client.companyName || "Not provided"}
                  </span>
                )}
              </div>

              {/* Position */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Position
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.position}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        position: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.position || "Not provided"}
                  </span>
                )}
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Location
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.location}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        location: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span
                    className="font-medium truncate select-text"
                    title={client.location}
                  >
                    {client.location || "Not provided"}
                  </span>
                )}
              </div>

              {/* Website */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Website
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="url"
                    value={editedClient.website}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        website: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span
                    className="font-medium truncate select-text"
                    title={client.website}
                  >
                    {client.website || "Not provided"}
                  </span>
                )}
              </div>

              {/* Timezone */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Timezone
                </span>
                {isEditingClient ? (
                  <input
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    type="text"
                    value={editedClient.timezone}
                    onChange={(e) =>
                      setEditedClient({
                        ...editedClient,
                        timezone: e.target.value,
                      })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.timezone || "Not provided"}
                  </span>
                )}
              </div>

              {/* Bio */}
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">Bio</span>
                {isEditingClient ? (
                  <textarea
                    className="px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="3"
                    value={editedClient.bio}
                    onChange={(e) =>
                      setEditedClient({ ...editedClient, bio: e.target.value })
                    }
                  />
                ) : (
                  <span className="font-medium select-text">
                    {client.bio || "Not provided"}
                  </span>
                )}
              </div>
            </div>
          </div>
        );

      case "otherInfo":
        return (
          <div className="card p-6 shadow-md">
            <h3 className="text-lg font-semibold text-primary-600 mb-4">
              Other Information
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <span className="font-medium text-green-600">
                  {client.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                <span className="text-gray-600 dark:text-gray-400">
                  Signup Date
                </span>
                <span className="font-medium">{client.signupDate}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm py-2 px-2">
                <span className="text-gray-600 dark:text-gray-400">
                  Client For
                </span>
                <span className="font-medium">{client.clientFor}</span>
              </div>
              <div className="space-y-2 mt-4">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Last Login
                </div>
                <div className="text-sm py-2 px-2 bg-gray-50/50 dark:bg-gray-800/30 rounded">
                  <span className="text-gray-600 dark:text-gray-400">
                    Date:{" "}
                  </span>
                  <span className="font-medium">{client.lastLoginDate}</span>
                </div>
                <div className="text-sm py-2 px-2">
                  <span className="text-gray-600 dark:text-gray-400">
                    IP Address:{" "}
                  </span>
                  <span className="font-medium break-all">
                    {client.lastLoginIp}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case "adminActions":
        // Only show admin actions for admin/staff users
        if (!user || user.role === "customer") return null;

        return (
          <div className="card p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
              Admin Actions
            </h3>
            <div className="space-y-3">
              <button
                className={`btn w-full ${
                  client.isReseller
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white`}
                onClick={() => {
                  setPendingResellerAction(!client.isReseller);
                  setShowResellerConfirmModal(true);
                }}
              >
                <i className="fas fa-user-tag mr-2" />
                {client.isReseller ? "Remove Reseller" : "Make Reseller"}
              </button>
              <button
                className={`btn w-full text-white ${
                  client.status === "SUSPENDED"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-600 hover:bg-gray-700"
                }`}
                onClick={() => {
                  setPendingSuspendAction(client.status !== "SUSPENDED");
                  setShowSuspendConfirmModal(true);
                }}
              >
                <i
                  className={`fas ${client.status === "SUSPENDED" ? "fa-play-circle" : "fa-pause-circle"} mr-2`}
                />
                {client.status === "SUSPENDED"
                  ? "Unsuspend Account"
                  : "Suspend Account"}
              </button>
              <button
                className="btn w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!client.email || client.email === "Not provided"}
                onClick={() => {
                  if (!client.email || client.email === "Not provided") {
                    toast.error("No email address on file for this customer");

                    return;
                  }
                  setShowCreateTicketModal(true);
                }}
              >
                <i className="fas fa-ticket-alt mr-2" />
                Create Ticket &amp; Notify
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const tabs = useMemo(
    () => [
      { id: "summary", label: "Overview", icon: "fas fa-info-circle" },
      {
        id: "invoices",
        label: "Invoices",
        icon: "fas fa-file-invoice-dollar",
      },
      { id: "subscriptions", label: "Subscriptions", icon: "fas fa-key" },
      { id: "redeemcodes", label: "LunaKeys™", icon: "fas fa-gift" },
      { id: "tickets", label: "Tickets", icon: "fas fa-ticket-alt" },
      { id: "notes", label: "Notes", icon: "fas fa-sticky-note" },
      ...(showResellerLogTab
        ? [{ id: "log", label: "Log", icon: "fas fa-history" }]
        : []),
    ],
    [showResellerLogTab]
  );

  // Most recently created/modified note (API orders by updated_at DESC, and
  // updated_at === created_at until a note is edited).
  const latestNote = customerNotes.length > 0 ? customerNotes[0] : null;
  const redeemCodeStatusOptions = useMemo(
    () => getRedeemCodeStatusOptions(lunaKeyTableCodes),
    [lunaKeyTableCodes]
  );
  const selectedRedeemCodeStatusSet = useMemo(
    () =>
      new Set(
        (redeemCodesStatusFilter || [])
          .map(normalizeRedeemCodeStatus)
          .filter(Boolean)
      ),
    [redeemCodesStatusFilter]
  );
  const redeemCodeStatusFilterLabel = useMemo(
    () =>
      getRedeemCodeStatusFilterLabel(
        redeemCodesStatusFilter,
        redeemCodeStatusOptions
      ),
    [redeemCodeStatusOptions, redeemCodesStatusFilter]
  );
  const paginatedRedeemCodes = useMemo(
    () =>
      sortedRedeemCodes.slice(
        (redeemCodesPage - 1) * redeemCodesPerPage,
        redeemCodesPage * redeemCodesPerPage
      ),
    [redeemCodesPage, redeemCodesPerPage, sortedRedeemCodes]
  );
  const resellerLogTotalPages = resellerLogsPagination.pages || 0;
  const resellerLogPageNumbers = useMemo(
    () =>
      getVisiblePageNumbers(
        resellerLogsPagination.page || resellerLogsPage,
        resellerLogTotalPages
      ),
    [resellerLogTotalPages, resellerLogsPage, resellerLogsPagination.page]
  );
  const resellerLogFirstItem =
    resellerLogsPagination.total > 0
      ? (resellerLogsPagination.page - 1) * resellerLogsPagination.limit + 1
      : 0;
  const resellerLogLastItem =
    resellerLogsPagination.total > 0
      ? Math.min(
          resellerLogFirstItem + resellerLogs.length - 1,
          resellerLogsPagination.total
        )
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fas fa-spinner fa-spin text-3xl text-primary-600" />
      </div>
    );
  }

  // Handle error state - if client is null after loading
  if (!client) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-5xl text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Unable to Load User/Customer Data
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The requested user or customer could not be found.
          </p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {client.firstName}
            </h1>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                client.type === "Reseller" && client.resellerType === "module"
                  ? "bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 dark:from-indigo-900 dark:to-indigo-800 dark:text-indigo-200"
                  : client.type === "Reseller"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
                    : client.type === "Premium"
                      ? "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 dark:from-purple-900 dark:to-purple-800 dark:text-purple-200"
                      : client.type === "Trial"
                        ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {client.type === "Reseller" && client.resellerType === "module"
                ? "Module Reseller"
                : client.type || "Free User"}
            </span>
            {client.resellerManaged && <ResellerManagedBadge size="lg" />}
            {client.resellerTier && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                {client.resellerTier}
              </span>
            )}
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                client.status === "SUSPENDED"
                  ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                  : client.status === "INACTIVE"
                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                    : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              }`}
            >
              {client.status === "SUSPENDED"
                ? "Suspended"
                : client.status === "INACTIVE"
                  ? "Inactive"
                  : "Active"}
            </span>
            {transactions?.some((t) => t.metadata?.platform === "apple") && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900">
                <i className="fab fa-apple mr-1.5" />
                Apple
              </span>
            )}
          </div>
          {user?.role === "admin" && (
            <button
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isStartingImpersonation}
              onClick={async () => {
                if (isStartingImpersonation) return;

                setIsStartingImpersonation(true);
                try {
                  const result = await startImpersonation(id, {
                    returnTo: `${location.pathname}${location.search}${location.hash}`,
                  });

                  if (!result.success) {
                    toast.error(result.error || "Failed to impersonate user");
                  }
                } catch (error) {
                  toast.error("Failed to impersonate user");
                } finally {
                  setIsStartingImpersonation(false);
                }
              }}
            >
              {isStartingImpersonation ? (
                <>
                  <i className="fas fa-spinner fa-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <i className="fas fa-user-secret" />
                  Login & Impersonate
                </>
              )}
            </button>
          )}
        </div>

        {/* Customer Details */}
        <div className="mb-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Registration ID #{client.id}
              </p>
              {client.companyName && (
                <div className="flex items-center gap-3 mt-1">
                  <i className="fas fa-building text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {client.companyName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hidden Dropdown - keeping for navigation functionality */}
        <div className="hidden">
          <div className="relative w-full md:w-96">
            <input
              className="input w-full pl-10 pr-10 cursor-pointer"
              placeholder="Search clients..."
              type="text"
              value={
                showDropdown
                  ? searchTerm
                  : `${client.firstName} ${client.lastName} - #${client.id}`
              }
              onBlur={() => {
                setTimeout(() => setShowDropdown(false), 200);
              }}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                setShowDropdown(true);
                setSearchTerm("");
              }}
            />
            {showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {filteredClients.length > 0 ? (
                  filteredClients.map((c) => (
                    <button
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between ${
                        c.id === client.id
                          ? "bg-primary-50 dark:bg-primary-900/20"
                          : ""
                      }`}
                      key={c.id}
                      type="button"
                      onMouseDown={() => handleClientChange(c.id)}
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {c.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {c.email}
                        </div>
                        {c.company && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {c.company}
                          </div>
                        )}
                      </div>
                      {c.id === client.id && (
                        <i className="fas fa-check text-primary-600" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    No clients found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Latest admin note - non-blocking banner */}
      {latestNote && (
        <div className="mb-6 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-sticky-note text-amber-600 dark:text-amber-400 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              Latest Admin Note
            </p>
            <p
              className="mt-0.5 text-sm text-gray-800 dark:text-gray-200 truncate"
              title={latestNote.message}
            >
              {latestNote.message}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {latestNote.author?.displayName || "Unknown admin"} -{" "}
              {latestNote.isEdited
                ? `Updated ${formatNoteTimestamp(latestNote.updatedAt)}`
                : `Created ${formatNoteTimestamp(latestNote.createdAt)}`}
            </p>
          </div>
          <button
            className="flex-shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
            type="button"
            onClick={() => setActiveTab("notes")}
          >
            View all
            <i className="fas fa-chevron-right ml-1 text-[10px]" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 cursor-pointer shadow-md hover:shadow-xl transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.1 }}
          onClick={() => setActiveTab("summary")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Spent
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {client?.totalSpent || "$0.00"}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-dollar-sign text-green-600 dark:text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 cursor-pointer shadow-md hover:shadow-xl transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.2 }}
          onClick={() => setActiveTab("tickets")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Tickets
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {client?.totalTickets || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-ticket-alt text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 cursor-pointer shadow-md hover:shadow-xl transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.3 }}
          onClick={() => setActiveTab("invoices")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Invoices
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {client?.payments || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-file-invoice text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 cursor-pointer shadow-md hover:shadow-xl transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.4 }}
          onClick={() => handleLunaKeyStatusSummaryClick("CLAIMED")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Claimed LunaKeys™
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {lunaKeyStatusCounts.claimed}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-gift text-green-600 dark:text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 cursor-pointer shadow-md hover:shadow-xl transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.5 }}
          onClick={() => handleLunaKeyStatusSummaryClick("UNCLAIMED")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Unclaimed LunaKeys™
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {lunaKeyStatusCounts.unclaimed}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-gift text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs Navigation */}
      <div className="card rounded-xl mb-6 shadow-md">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => (
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`${tab.icon} mr-2`} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area - Transparent Background */}
      <div>
        {/* Main Content Grid */}
        {activeTab === "summary" && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Column 1 */}
              <StrictModeDroppable droppableId="column1">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-4"
                  >
                    {widgets.column1.map((widgetId, index) => (
                      <Draggable
                        draggableId={widgetId}
                        index={index}
                        key={widgetId}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${snapshot.isDragging ? "opacity-50" : ""}`}
                          >
                            {renderWidget(widgetId)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>

              {/* Column 2 */}
              <StrictModeDroppable droppableId="column2">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-4"
                  >
                    {widgets.column2.map((widgetId, index) => (
                      <Draggable
                        draggableId={widgetId}
                        index={index}
                        key={widgetId}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${snapshot.isDragging ? "opacity-50" : ""}`}
                          >
                            {renderWidget(widgetId)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>

              {/* Column 3 */}
              <StrictModeDroppable droppableId="column3">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-4"
                  >
                    {widgets.column3.map((widgetId, index) => (
                      <Draggable
                        draggableId={widgetId}
                        index={index}
                        key={widgetId}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${snapshot.isDragging ? "opacity-50" : ""}`}
                          >
                            {renderWidget(widgetId)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            </div>
          </DragDropContext>
        )}
        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="card shadow-md">
            {loadingTabData ? (
              <div className="p-6 text-center">
                <i className="fas fa-spinner fa-spin text-2xl text-primary-600" />
                <p className="mt-2 text-gray-500">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-6 text-center">
                <i className="fas fa-file-invoice text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500">
                  No invoices found for this customer
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Invoice #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        LunaKeys™
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {invoices.map((invoice, index) => (
                      <tr
                        className={`${
                          index % 2 === 0
                            ? ""
                            : "bg-gray-50/50 dark:bg-gray-800/30"
                        } hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer transition-colors`}
                        key={invoice.transactionId}
                        onClick={() => handleViewInvoice(invoice.transactionId)}
                      >
                        <td className="px-4 py-3 text-sm font-medium">
                          {invoice.id}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {invoice.issueDate}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            <i className="fas fa-key mr-1.5" />
                            {invoice.redeemCodeCount || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          ${invoice.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === "paid"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : invoice.status === "overdue"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                  : invoice.status === "sent" ||
                                      invoice.status === "viewed"
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {invoice.status}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            onClick={() =>
                              handleViewInvoice(invoice.transactionId)
                            }
                          >
                            <i className="fas fa-eye" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <SubscriptionLunaKeysTable
            allowImmediateCancellation
            cancelDescription="This schedules Stripe billing to stop at the end of the current billing period. The customer's LunaKey stays active until then."
            cancelSubscriptionLunaKey={cancelCustomerSubscriptionLunaKey}
            emptyMessage="No subscription LunaKeys found for this customer"
            fetchSubscriptionLunaKeys={fetchCustomerSubscriptionLunaKeys}
            immediateCancelDescription="This cancels Stripe billing immediately and deactivates the LunaKey tied to this customer's subscription right away."
            isModuleReseller={
              client?.type === "Reseller" && client?.resellerType === "module"
            }
            supportDescription="Module reseller package subscriptions include multiple LunaKeys, so cancellation remains a manual staff process."
            supportModalTitle="Manual Cancellation Required"
            title="Customer Subscriptions"
          />
        )}

        {/* Tickets Tab */}
        {activeTab === "tickets" && (
          <div className="card shadow-md">
            {loadingTabData ? (
              <div className="p-6 text-center">
                <i className="fas fa-spinner fa-spin text-2xl text-primary-600" />
                <p className="mt-2 text-gray-500">Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-center">
                <i className="fas fa-ticket-alt text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500">
                  No tickets found for this customer
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Ticket ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Subject
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Priority
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {tickets.map((ticket, index) => (
                      <tr
                        className={`${
                          index % 2 === 0
                            ? ""
                            : "bg-gray-50/50 dark:bg-gray-800/30"
                        } hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer transition-colors`}
                        key={ticket.ticket_id}
                        onClick={() =>
                          navigate(`/tickets/${ticket.ticket_number}`)
                        }
                      >
                        <td className="px-4 py-3 text-sm font-medium">
                          {ticket.ticket_number}
                        </td>
                        <td className="px-4 py-3 text-sm">{ticket.subject}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.status === "open"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : ticket.status === "closed"
                                  ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            }`}
                          >
                            {ticket.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.priority === "high" ||
                              ticket.priority === "urgent"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                : ticket.priority === "medium"
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                  : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                            }`}
                          >
                            {ticket.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {ticket.created_at
                            ? new Date(ticket.created_at).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === "notes" && (
          <CustomerNotesTab
            loading={notesLoading}
            notes={customerNotes}
            onCreateNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onUpdateNote={handleUpdateNote}
          />
        )}

        {/* Log Tab */}
        {activeTab === "log" && showResellerLogTab && (
          <div className="card shadow-md">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Reseller Logs</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing logs for {client.firstName} {client.lastName}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative">
                    <input
                      className="input w-full py-2 pl-10 pr-9 text-sm sm:w-72"
                      placeholder="Search reseller logs..."
                      type="text"
                      value={resellerLogsSearchQuery}
                      onChange={(event) => {
                        setResellerLogsSearchQuery(event.target.value);
                        setResellerLogsPage(1);
                      }}
                    />
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    {resellerLogsSearchQuery && (
                      <button
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        type="button"
                        onClick={() => {
                          setResellerLogsSearchQuery("");
                          setResellerLogsPage(1);
                        }}
                      >
                        <i className="fas fa-times text-sm" />
                      </button>
                    )}
                  </div>
                  <select
                    className="input py-2 text-sm"
                    value={resellerLogsPerPage}
                    onChange={(event) => {
                      setResellerLogsPerPage(Number(event.target.value));
                      setResellerLogsPage(1);
                    }}
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-primary-700 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                      <button
                        className="flex items-center gap-1"
                        type="button"
                        onClick={() => handleResellerLogSort("createdAt")}
                      >
                        Date
                        {resellerLogsSortField === "createdAt" && (
                          <i
                            className={`fas fa-chevron-${resellerLogsSortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                      <button
                        className="flex items-center gap-1"
                        type="button"
                        onClick={() => handleResellerLogSort("actionType")}
                      >
                        Action Type
                        {resellerLogsSortField === "actionType" && (
                          <i
                            className={`fas fa-chevron-${resellerLogsSortDirection === "asc" ? "up" : "down"} text-xs`}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {resellerLogsLoading ? (
                    <tr>
                      <td
                        className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                        colSpan="4"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="h-8 w-8 rounded-full border-b-2 border-primary-600 animate-spin" />
                          <span>Loading reseller logs...</span>
                        </div>
                      </td>
                    </tr>
                  ) : resellerLogs.length > 0 ? (
                    resellerLogs.map((log, index) => (
                      <tr
                        className={
                          index % 2 === 0
                            ? ""
                            : "bg-gray-50/50 dark:bg-gray-800/30"
                        }
                        key={log.id}
                      >
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {log.createdAt
                            ? new Date(log.createdAt).toLocaleString()
                            : "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            {log.actionType || "unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {log.description || "No description"}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-600 dark:text-gray-400">
                          {log.actionSource || "unknown"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                        colSpan="4"
                      >
                        <i className="fas fa-history text-3xl mb-2" />
                        <p>No reseller logs available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {resellerLogsPagination.total > 0
                  ? `Showing ${resellerLogFirstItem} to ${resellerLogLastItem} of ${resellerLogsPagination.total} logs`
                  : "0 logs found"}
              </span>
              {resellerLogTotalPages > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={resellerLogsPage === 1}
                    type="button"
                    onClick={() =>
                      setResellerLogsPage((page) => Math.max(page - 1, 1))
                    }
                  >
                    Previous
                  </button>
                  {resellerLogPageNumbers.map((page) => (
                    <button
                      className={`px-3 py-1 text-sm border rounded-lg transition-colors ${
                        resellerLogsPage === page
                          ? "bg-primary-600 text-white border-primary-600"
                          : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                      key={page}
                      type="button"
                      onClick={() => setResellerLogsPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={resellerLogsPage >= resellerLogTotalPages}
                    type="button"
                    onClick={() =>
                      setResellerLogsPage((page) =>
                        Math.min(page + 1, resellerLogTotalPages)
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* LunaKeys Tab */}
        {activeTab === "redeemcodes" && (
          <div className="card p-6 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">LunaKeys™</h3>
              {lunaKeyTableCodes.length > 0 && (
                <div className="flex items-center gap-4">
                  {/* Search Input */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        className="input py-1 px-3 pr-8 text-sm"
                        placeholder="Search codes..."
                        type="text"
                        value={redeemCodesSearchQuery}
                        onChange={(e) => {
                          setRedeemCodesSearchQuery(e.target.value);
                          setRedeemCodesPage(1);
                        }}
                      />
                      {redeemCodesSearchQuery && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          onClick={() => {
                            setRedeemCodesSearchQuery("");
                            setRedeemCodesPage(1);
                          }}
                        >
                          <i className="fas fa-times text-xs" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    className="relative flex items-center gap-2"
                    ref={redeemCodeStatusDropdownRef}
                  >
                    <label
                      className="text-sm text-gray-600 dark:text-gray-400"
                      id="redeem-code-status-filter-label"
                    >
                      Status:
                    </label>
                    <button
                      aria-expanded={showRedeemCodesStatusDropdown}
                      aria-haspopup="true"
                      aria-labelledby="redeem-code-status-filter-label"
                      className="input py-1 px-2 text-sm min-w-[11rem] max-w-[14rem] flex items-center justify-between gap-2"
                      type="button"
                      onClick={() =>
                        setShowRedeemCodesStatusDropdown((open) => !open)
                      }
                    >
                      <span className="truncate">
                        {redeemCodeStatusFilterLabel}
                      </span>
                      <i
                        className={`fas fa-chevron-${showRedeemCodesStatusDropdown ? "up" : "down"} text-xs text-gray-400`}
                      />
                    </button>
                    {showRedeemCodesStatusDropdown && (
                      <div
                        aria-labelledby="redeem-code-status-filter-label"
                        className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="max-h-72 overflow-y-auto py-1">
                          {redeemCodeStatusOptions.map((option) => (
                            <label
                              className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/70"
                              key={option.value}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <input
                                  aria-label={`Filter ${option.label} LunaKeys`}
                                  checked={selectedRedeemCodeStatusSet.has(
                                    option.value
                                  )}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                  type="checkbox"
                                  onChange={() =>
                                    handleRedeemCodeStatusToggle(option.value)
                                  }
                                />
                                <span className="truncate">{option.label}</span>
                              </span>
                              <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">
                                {option.count}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Show:
                    </label>
                    <select
                      className="input py-1 px-2 text-sm"
                      value={redeemCodesPerPage}
                      onChange={(e) => {
                        setRedeemCodesPerPage(Number(e.target.value));
                        setRedeemCodesPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            {lunaKeyTableCodes.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                          Code
                        </th>
                        <th
                          className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleRedeemCodesSort("type")}
                        >
                          <div className="flex items-center gap-1">
                            Type
                            {redeemCodesSortField === "type" && (
                              <i
                                className={`fas fa-chevron-${redeemCodesSortDirection === "asc" ? "up" : "down"} text-xs`}
                              />
                            )}
                          </div>
                        </th>
                        <th
                          className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleRedeemCodesSort("status")}
                        >
                          <div className="flex items-center gap-1">
                            Status
                            {redeemCodesSortField === "status" && (
                              <i
                                className={`fas fa-chevron-${redeemCodesSortDirection === "asc" ? "up" : "down"} text-xs`}
                              />
                            )}
                          </div>
                        </th>
                        <th
                          className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleRedeemCodesSort("managed")}
                        >
                          <div className="flex items-center gap-1">
                            Managed
                            {redeemCodesSortField === "managed" && (
                              <i
                                className={`fas fa-chevron-${redeemCodesSortDirection === "asc" ? "up" : "down"} text-xs`}
                              />
                            )}
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                          Playlist
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                          Devices
                        </th>
                        <th
                          className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleRedeemCodesSort("redeemed")}
                        >
                          <div className="flex items-center gap-1">
                            Redeemed Date
                            {redeemCodesSortField === "redeemed" && (
                              <i
                                className={`fas fa-chevron-${redeemCodesSortDirection === "asc" ? "up" : "down"} text-xs`}
                              />
                            )}
                          </div>
                        </th>
                        <th
                          className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleRedeemCodesSort("expires")}
                        >
                          <div className="flex items-center gap-1">
                            Expires
                            {redeemCodesSortField === "expires" && (
                              <i
                                className={`fas fa-chevron-${redeemCodesSortDirection === "asc" ? "up" : "down"} text-xs`}
                              />
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRedeemCodes.length > 0 ? (
                        paginatedRedeemCodes.map((code) => {
                          const linkedPlaylists =
                            getRedeemCodeLinkedPlaylists(code);
                          const primaryPlaylist = linkedPlaylists[0];
                          const linkedPlaylistCount =
                            code.linkedPlaylistCount ?? linkedPlaylists.length;
                          const devicesCount =
                            primaryPlaylist?.devicesCount ??
                            primaryPlaylist?.deviceCount ??
                            0;
                          const isResellerManaged =
                            isResellerManagedLunaKey(code);

                          return (
                            <tr
                              className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                              key={code.redeem_code_id}
                              onClick={() =>
                                fetchRedeemCodeDetails(code.redeem_code_id)
                              }
                            >
                              <td className="py-3 px-4">
                                <span className="font-mono text-sm">
                                  {code.code}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm capitalize">
                                  {code.redeem_type.replace("-", " ")}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRedeemCodeStatusClass(
                                    code.status
                                  )}`}
                                >
                                  {formatRedeemCodeStatus(code.status)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {isResellerManaged ? (
                                  <div className="flex flex-col items-start gap-1">
                                    <ResellerManagedBadge size="sm" />
                                    {(code.managedByName ||
                                      code.managedByEmail) && (
                                      <span
                                        className="max-w-[180px] truncate text-xs text-gray-500 dark:text-gray-400"
                                        title={code.managedByEmail || undefined}
                                      >
                                        {code.managedByName ||
                                          code.managedByEmail}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <CustomerManagedBadge size="sm" />
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {primaryPlaylist ? (
                                  <div className="min-w-0 max-w-[220px]">
                                    <div
                                      className="truncate font-medium text-gray-800 dark:text-gray-200"
                                      title={getPlaylistDisplayName(
                                        primaryPlaylist
                                      )}
                                    >
                                      {getPlaylistDisplayName(primaryPlaylist)}
                                      {linkedPlaylistCount > 1
                                        ? ` +${linkedPlaylistCount - 1}`
                                        : ""}
                                    </div>
                                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                                      {primaryPlaylist.domain ||
                                        "Unknown domain"}
                                      {primaryPlaylist.username
                                        ? ` / ${primaryPlaylist.username}`
                                        : ""}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">
                                    -
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {primaryPlaylist && !isResellerManaged ? (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!devicesCount}
                                    title={
                                      devicesCount
                                        ? "View devices logged in on this playlist"
                                        : "No active devices"
                                    }
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleShowPlaylistDevices(
                                        primaryPlaylist
                                      );
                                    }}
                                  >
                                    <i className="fas fa-tv text-xs" />
                                    {devicesCount}
                                  </button>
                                ) : (
                                  <span className="text-sm text-gray-400 dark:text-gray-500">
                                    -
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {code.date_redeemed
                                  ? new Date(
                                      code.date_redeemed
                                    ).toLocaleDateString()
                                  : "-"}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {code.expires_at
                                  ? new Date(
                                      code.expires_at
                                    ).toLocaleDateString()
                                  : "Never"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="py-8 px-4 text-center text-sm text-gray-500 dark:text-gray-400"
                            colSpan={8}
                          >
                            No LunaKeys match the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {Math.ceil(sortedRedeemCodes.length / redeemCodesPerPage) >
                  1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing{" "}
                      {Math.min(
                        (redeemCodesPage - 1) * redeemCodesPerPage + 1,
                        sortedRedeemCodes.length
                      )}{" "}
                      to{" "}
                      {Math.min(
                        redeemCodesPage * redeemCodesPerPage,
                        sortedRedeemCodes.length
                      )}{" "}
                      of {sortedRedeemCodes.length} codes
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={redeemCodesPage === 1}
                        onClick={() => setRedeemCodesPage(redeemCodesPage - 1)}
                      >
                        Previous
                      </button>

                      {/* Page Numbers */}
                      {(() => {
                        const totalPages = Math.ceil(
                          sortedRedeemCodes.length / redeemCodesPerPage
                        );
                        const pages = [];
                        const maxVisible = 3;
                        let startPage = Math.max(
                          1,
                          redeemCodesPage - Math.floor(maxVisible / 2)
                        );
                        const endPage = Math.min(
                          totalPages,
                          startPage + maxVisible - 1
                        );

                        // Adjust start if we're near the end
                        if (endPage - startPage < maxVisible - 1) {
                          startPage = Math.max(1, endPage - maxVisible + 1);
                        }

                        // Show page range
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              className={`px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                redeemCodesPage === i
                                  ? "bg-primary-600 text-white border-primary-600"
                                  : ""
                              }`}
                              key={i}
                              onClick={() => setRedeemCodesPage(i)}
                            >
                              {i}
                            </button>
                          );
                        }

                        // Show ... with total pages if there are more pages
                        if (totalPages > maxVisible) {
                          pages.push(
                            <div
                              className="relative flex items-center gap-2"
                              key="ellipsis"
                            >
                              {showPageInput ? (
                                <input
                                  autoFocus
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                  max={totalPages}
                                  min={1}
                                  placeholder="..."
                                  type="number"
                                  value={pageInputValue}
                                  onBlur={() => {
                                    setShowPageInput(false);
                                    setPageInputValue("");
                                  }}
                                  onChange={(e) =>
                                    setPageInputValue(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const page = parseInt(pageInputValue, 10);

                                      if (page >= 1 && page <= totalPages) {
                                        setRedeemCodesPage(page);
                                      }
                                      setShowPageInput(false);
                                      setPageInputValue("");
                                    }
                                  }}
                                />
                              ) : (
                                <button
                                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                  onClick={() => setShowPageInput(true)}
                                >
                                  ...
                                </button>
                              )}
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {totalPages}
                              </span>
                            </div>
                          );
                        }

                        return pages;
                      })()}

                      <button
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={
                          redeemCodesPage ===
                          Math.ceil(
                            sortedRedeemCodes.length / redeemCodesPerPage
                          )
                        }
                        onClick={() => setRedeemCodesPage(redeemCodesPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No redeem codes found for this customer.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Email View Modal */}
      {showEmailView && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold">Email Details</h2>
              <button
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => {
                  setShowEmailView(false);
                  setSelectedEmail(null);
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      From
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                      {selectedEmail.from || "Unknown Sender"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      To
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                      {selectedEmail.to || "Unknown Recipient"}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Date
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {selectedEmail.date || "Unknown Date"}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Subject
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded font-medium">
                    {selectedEmail.subject || "No Subject"}
                    {selectedEmail.hasAttachment && (
                      <i className="fas fa-paperclip text-gray-400 ml-2" />
                    )}
                  </div>
                </div>

                {selectedEmail.hasAttachment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Attachments
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-file-pdf text-red-600" />
                        <span className="text-sm">
                          invoice_{selectedEmail.date.replace(/[/\s:]/g, "_")}
                          .pdf
                        </span>
                        <button className="text-primary-600 hover:text-primary-700 text-sm ml-auto">
                          <i className="fas fa-download mr-1" /> Download
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Message
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                    {selectedEmail.body ? (
                      <div className="whitespace-pre-wrap break-words">
                        {selectedEmail.body}
                      </div>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400 italic">
                        No message content available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowEmailView(false);
                  setSelectedEmail(null);
                }}
              >
                Close
              </button>
              <button className="btn btn-primary">
                <i className="fas fa-reply mr-2" />
                Reply
              </button>
              <button className="btn btn-primary">
                <i className="fas fa-share mr-2" />
                Forward
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Product/Service</h2>
              <button
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowProductModal(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Product Name
                  </label>
                  <input
                    className="input w-full"
                    type="text"
                    value={newProduct.productName}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        productName: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Product Type
                  </label>
                  <select
                    className="input w-full"
                    value={newProduct.productType}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        productType: e.target.value,
                      })
                    }
                  >
                    <option value="app_access">App Access</option>
                    <option value="redeem_code">LunaKey™</option>
                    <option value="subscription">Subscription</option>
                    <option value="one_time">One Time</option>
                    <option value="addon">Addon</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    className="input w-full"
                    type="number"
                    value={newProduct.quantity}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        quantity: parseInt(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Price
                  </label>
                  <input
                    className="input w-full"
                    step="0.01"
                    type="number"
                    value={newProduct.price}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        price: parseFloat(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Recurring Amount
                  </label>
                  <input
                    className="input w-full"
                    step="0.01"
                    type="number"
                    value={newProduct.recurringAmount}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        recurringAmount: parseFloat(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Billing Cycle
                  </label>
                  <select
                    className="input w-full"
                    value={newProduct.billingCycle}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        billingCycle: e.target.value,
                      })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                    <option value="free_trial">Free Trial</option>
                    <option value="one_time">One Time</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Package Type
                  </label>
                  <select
                    className="input w-full"
                    value={newProduct.packageType}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        packageType: e.target.value,
                      })
                    }
                  >
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="vip">VIP</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Payment Method
                  </label>
                  <select
                    className="input w-full"
                    value={newProduct.paymentMethod}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        paymentMethod: e.target.value,
                      })
                    }
                  >
                    <option value="stripe">Stripe</option>
                    <option value="authorize">Authorize</option>
                    <option value="paypal">PayPal</option>
                    <option value="manual">Manual</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Promotion Code
                  </label>
                  <input
                    className="input w-full"
                    placeholder="Optional"
                    type="text"
                    value={newProduct.promotionCode}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        promotionCode: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn btn-ghost"
                onClick={() => setShowProductModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateProduct}>
                <i className="fas fa-plus mr-2" />
                Create Product
              </button>
            </div>
          </div>
        </div>
      )}

      <PlaylistDevicesModal
        devices={playlistDevicesModalItems}
        loading={playlistDevicesModalLoading}
        open={showPlaylistDevicesModal}
        playlist={playlistDevicesModalPlaylist}
        onClose={closePlaylistDevicesModal}
      />

      {/* LunaKey™ Details Modal */}
      <AnimatePresence>
        {showRedeemCodeModal && selectedRedeemCode && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowRedeemCodeModal(false);
                setSelectedRedeemCode(null);
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                setShowRedeemCodeModal(false);
                setSelectedRedeemCode(null);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800/95 dark:backdrop-blur-sm rounded-xl p-6 w-full max-w-2xl relative shadow-2xl"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => {
                  setShowRedeemCodeModal(false);
                  setSelectedRedeemCode(null);
                }}
              >
                <i className="fas fa-times text-lg" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-gift text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  LunaKey&trade; Details
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Complete information about this LunaKey
                </p>
              </div>

              {/* Code Display */}
              <button
                className="w-full bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6 mb-6 border border-purple-200 dark:border-purple-800 cursor-pointer hover:border-purple-300 dark:hover:border-purple-700 transition-colors group"
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(selectedRedeemCode.code);
                  toast.success("Code copied to clipboard!");
                }}
              >
                <div className="text-center flex items-center justify-center gap-2">
                  <p className="font-mono text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {selectedRedeemCode.code}
                  </p>
                  <i className="fas fa-copy text-lg opacity-0 group-hover:opacity-100 transition-opacity text-purple-600 dark:text-purple-400" />
                </div>
              </button>

              {/* Details Grid */}
              <div className="space-y-4 mb-6">
                {/* Type and Status Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Type
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white capitalize">
                      {selectedRedeemCode.redeem_type.replace("-", " ")}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Status
                    </p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getRedeemCodeStatusClass(
                        selectedRedeemCode.status
                      )}`}
                    >
                      {formatRedeemCodeStatus(selectedRedeemCode.status)}
                    </span>
                  </div>
                </div>

                {/* Expires and Auto-Renew Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Expires
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {selectedRedeemCode.expires_at ? (
                        <>
                          {new Date(
                            selectedRedeemCode.expires_at
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                          <br />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(
                              selectedRedeemCode.expires_at
                            ).toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                              timeZoneName: "short",
                            })}
                          </span>
                        </>
                      ) : (
                        "Never"
                      )}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Auto-Renew
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {selectedRedeemCode.autoRenew ? (
                        <span className="text-green-600 dark:text-green-400">
                          <i className="fas fa-check-circle mr-1" />
                          Enabled
                        </span>
                      ) : (
                        <span className="text-gray-600 dark:text-gray-400">
                          <i className="fas fa-times-circle mr-1" />
                          Disabled
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Linked playlists */}
                {(() => {
                  const linkedPlaylists =
                    getRedeemCodeLinkedPlaylists(selectedRedeemCode);
                  const linkedPlaylistCount =
                    selectedRedeemCode.linkedPlaylistCount ??
                    linkedPlaylists.length;

                  if (
                    linkedPlaylists.length === 0 &&
                    selectedRedeemCode.status !== "CLAIMED"
                  ) {
                    return null;
                  }

                  return (
                    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.02]">
                      <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-white/10 px-3 py-2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                          <i className="fas fa-layer-group text-[10px]" />
                          Linked Playlists
                        </div>
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                          {linkedPlaylistCount}
                        </span>
                      </div>
                      {linkedPlaylists.length > 0 ? (
                        <div className="max-h-44 overflow-y-auto divide-y divide-gray-200 dark:divide-white/10">
                          {linkedPlaylists.map((playlist, index) => (
                            <div
                              className="px-3 py-2.5"
                              key={
                                getRedeemCodePlaylistKey(playlist) ||
                                `playlist-${index}`
                              }
                            >
                              <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {playlist.name ||
                                  playlist.playlistName ||
                                  playlist.accountName ||
                                  "Playlist"}
                              </div>
                              <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-gray-500 dark:text-gray-400">
                                <span className="min-w-0 truncate">
                                  <i className="fas fa-globe text-[10px] mr-1.5 opacity-60" />
                                  {playlist.domain || "Unknown domain"}
                                </span>
                                <span className="min-w-0 truncate">
                                  <i className="fas fa-at text-[10px] mr-1.5 opacity-60" />
                                  {playlist.username || "Unknown username"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          No linked playlists found
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                  onClick={() => {
                    setShowRedeemCodeModal(false);
                    setSelectedRedeemCode(null);
                  }}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reseller Confirmation Modal */}
      <AnimatePresence>
        {showResellerConfirmModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            role="button"
            style={{ zIndex: 99999 }}
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowResellerConfirmModal(false);
                setPendingResellerAction(null);
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                setShowResellerConfirmModal(false);
                setPendingResellerAction(null);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800/95 rounded-2xl p-8 w-full max-w-md relative shadow-2xl"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => {
                  setShowResellerConfirmModal(false);
                  setPendingResellerAction(null);
                }}
              >
                <i className="fas fa-times text-lg" />
              </button>

              {/* Icon and Title */}
              <div className="text-center mb-6">
                <div
                  className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    pendingResellerAction
                      ? "bg-blue-100 dark:bg-blue-900/30"
                      : "bg-red-100 dark:bg-red-900/30"
                  }`}
                >
                  <i
                    className={`fas fa-user-tag text-3xl ${
                      pendingResellerAction
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                  {pendingResellerAction ? "Make Reseller" : "Remove Reseller"}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {pendingResellerAction
                    ? `Select the reseller type for ${client.firstName} ${client.lastName}`
                    : `Are you sure you want to remove ${client.firstName} ${client.lastName} as a reseller?`}
                </p>
              </div>

              {pendingResellerAction ? (
                <>
                  {/* Reseller Type Selection */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      className="flex flex-col items-center p-5 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      onClick={() =>
                        handleUpdateResellerStatus({
                          isReseller: true,
                          resellerType: "regular",
                          successMessage: `${client.firstName} ${client.lastName} has been made a regular reseller`,
                        })
                      }
                    >
                      <i className="fas fa-store text-2xl text-blue-600 dark:text-blue-400 mb-3" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        Regular
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Dashboard reseller
                      </span>
                    </button>
                    <button
                      className="flex flex-col items-center p-5 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                      onClick={() =>
                        handleUpdateResellerStatus({
                          isReseller: true,
                          resellerType: "module",
                          successMessage: `${client.firstName} ${client.lastName} has been made a module reseller`,
                        })
                      }
                    >
                      <i className="fas fa-plug text-2xl text-indigo-600 dark:text-indigo-400 mb-3" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        Module
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        WHMCS integration
                      </span>
                    </button>
                  </div>
                  <button
                    className="w-full px-6 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    onClick={() => {
                      setShowResellerConfirmModal(false);
                      setPendingResellerAction(null);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-blue-500 mr-3 mt-1" />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <p>
                          This will remove reseller privileges from this
                          customer. They will no longer be able to purchase
                          codes in bulk.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      onClick={() => {
                        setShowResellerConfirmModal(false);
                        setPendingResellerAction(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="flex-1 px-6 py-3 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                      onClick={() =>
                        handleUpdateResellerStatus({
                          isReseller: false,
                          successMessage: `${client.firstName} ${client.lastName} has been removed as a reseller`,
                        })
                      }
                    >
                      <i className="fas fa-times mr-2" />
                      Remove
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Suspend Confirmation Modal */}
      <AnimatePresence>
        {showSuspendConfirmModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            role="button"
            style={{ zIndex: 99999 }}
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSuspendConfirmModal(false);
                setPendingSuspendAction(null);
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                setShowSuspendConfirmModal(false);
                setPendingSuspendAction(null);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800/95 rounded-2xl p-8 w-full max-w-md relative shadow-2xl"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => {
                  setShowSuspendConfirmModal(false);
                  setPendingSuspendAction(null);
                }}
              >
                <i className="fas fa-times text-lg" />
              </button>

              {/* Icon and Title */}
              <div className="text-center mb-6">
                <div
                  className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    pendingSuspendAction
                      ? "bg-gray-100 dark:bg-gray-900/30"
                      : "bg-green-100 dark:bg-green-900/30"
                  }`}
                >
                  <i
                    className={`fas ${pendingSuspendAction ? "fa-pause-circle" : "fa-play-circle"} text-3xl ${
                      pendingSuspendAction
                        ? "text-gray-600 dark:text-gray-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                  {pendingSuspendAction
                    ? "Suspend Account"
                    : "Unsuspend Account"}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {pendingSuspendAction
                    ? `Are you sure you want to suspend ${client.firstName} ${client.lastName}'s account?`
                    : `Are you sure you want to unsuspend ${client.firstName} ${client.lastName}'s account?`}
                </p>
              </div>

              {/* Information Box */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <i
                    className={`fas fa-info-circle ${pendingSuspendAction ? "text-orange-500" : "text-blue-500"} mr-3 mt-1`}
                  />
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {pendingSuspendAction ? (
                      <p>
                        This will temporarily disable access to this customer's
                        account. They will not be able to use the service until
                        the account is unsuspended.
                      </p>
                    ) : (
                      <p>
                        This will restore access to this customer's account.
                        They will be able to use the service normally.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  onClick={() => {
                    setShowSuspendConfirmModal(false);
                    setPendingSuspendAction(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                    pendingSuspendAction
                      ? "bg-gray-600 hover:bg-gray-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                  onClick={handleSuspendToggle}
                >
                  <i
                    className={`fas ${pendingSuspendAction ? "fa-pause-circle" : "fa-play-circle"} mr-2`}
                  />
                  {pendingSuspendAction ? "Suspend" : "Unsuspend"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <NewTicketModal
        isOpen={showCreateTicketModal}
        prefilledCustomer={
          client?.id || client?.user_id || client?.registration_id
            ? {
                id: client.id || client.user_id || client.registration_id,
                name: [client.firstName, client.lastName]
                  .filter((part) => part && part !== "Not provided")
                  .join(" ")
                  .trim(),
                email:
                  client.email && client.email !== "Not provided"
                    ? client.email
                    : "",
              }
            : null
        }
        onClose={() => setShowCreateTicketModal(false)}
        onTicketCreated={() => {
          /* refresh customer tickets list if needed */
        }}
      />
    </div>
  );
};

export default CustomerDetails;
